import type { WireTypes } from '@flakiness/server/common/wireTypes.js';
import type { Git } from '@flakiness/server/node/git.js';
import { consume, ContextType } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { contexts } from '../contexts.js';
import { forgeUtils } from '../forgeUtils.js';
import { renderRelativeDate } from '../fragments.js';
import { PageCommit } from '../page-commit.js';
import { PagePull } from '../page-pull.js';
import { linkStyles } from './cssstyles.js';

@customElement('fk-commit')
class FKCommit extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) commit?: WireTypes.Commit;
  @property({ attribute: false }) href?: string;
  @property({ type: Boolean }) large?: boolean;

  private _renderRef(ref: Git.CommitRef) {
    if (ref.type === 'branch')
      return html`<branch-label .name=${ref.name} icon></branch-label>`;
    return html`<pr-label .number=${ref.number} .state=${ref.state}></pr-label>`;
  }

  private _renderShortSha(commit: WireTypes.Commit) {
    const shortsha = commit.commitId.substring(0, 7);
    if (!forgeUtils.hasSourceUrl(this._project))
      return html`<span class=shortsha>${shortsha}</span>`;
    return html`<a class=shortsha
      href=${forgeUtils.commitUrl(this._project, commit.commitId)}
      target=_blank
      title=${`View on ${forgeUtils.providerName(this._project)}`}
    >${shortsha}</a>`;
  }

  private _renderTitle(title: string) {
    const link = this.href ? this.href : this._project && this.commit ? PageCommit.url({
      orgSlug: this._project.org.orgSlug,
      projectSlug: this._project.projectSlug,
      sha: this.commit?.commitId,
      snapshotId: this._snapshotId,
    }) : nothing;
    let idx = 0;
    const parts = [];
    for (const matchResult of title.matchAll(/\b[0-9a-f]{40}\b|#\d+\b/gi)) {
      const shaOrPr = matchResult[0];
      const isSha = shaOrPr.length === 40;
      const isPr = shaOrPr.startsWith('#');
      
      const innerLink = this._project && isSha ? PageCommit.url({
        orgSlug: this._project.org.orgSlug,
        projectSlug: this._project.projectSlug,
        sha: shaOrPr,
        snapshotId: this._snapshotId,
      }) : this._project && isPr ? PagePull.url({
        orgSlug: this._project.org.orgSlug,
        projectSlug: this._project.projectSlug,
        pullRequest: parseInt(shaOrPr.substring(1), 10),
        snapshotId: this._snapshotId,
      }) : nothing;
      parts.push(html`<a href=${link}>${title.substring(idx, matchResult.index)}</a>`);
      parts.push(html`<a href=${innerLink} class=${classMap({
        'abbr-sha': isSha,
        'pr-link': isPr,
      })}>${isSha ? shaOrPr.substring(0, 7) : shaOrPr}</a>`);
      idx = matchResult.index + shaOrPr.length;
    }
    parts.push(html`<a href=${link}>${title.substring(idx)}</a>`);
    return html`<span class=commit-title>${parts}</span>`;
  }

  override render() {
    const commit = this.commit;
    if (!commit)
      return nothing;
    if (commit.isFakeCommit) {
      return html`
        <v-box style="gap: var(--sl-spacing-2x-small);">
          <a class=commit-title href=${this.href ? this.href : this._project && this.commit ? PageCommit.url({
            orgSlug: this._project.org.orgSlug,
            projectSlug: this._project.projectSlug,
            sha: this.commit.commitId,
            snapshotId: this._snapshotId,
          }) : nothing}>Unknown Commit</a>
          <h-box>
            <sl-icon library=boxicons name=bx-git-commit></sl-icon>
            ${this._renderShortSha(commit)}
            <slot name=details></slot>
          </h-box>
        </v-box>
      `
    }
    const title = commit.message.split('\n')[0].trim();

    const branches = commit.refs?.filter(ref => ref.type === 'branch') ?? [];
    const prs = commit.refs?.filter(ref => ref.type === 'pull-request') ?? [];
    return html`
      <v-box class=container>
        <h-box class=title-row>
          ${this._renderTitle(title)}
          ${branches.length ? html`
            <h-box class=refs-block>
              ${branches.map(ref => html`<branch-label .name=${ref.name} icon></branch-label>`)}
            </h-box>
          ` : nothing}
          ${prs.length
            ? prs.map(ref => html`<pr-label .number=${ref.number} .state=${ref.state}></pr-label>`)
            : nothing
          }
        </h-box>
        <h-box class=commit-description>
          <sl-avatar image=${commit.avatar_url} label=${commit.author} loading=lazy></sl-avatar>
          ${commit.author} committed
          ${renderRelativeDate(commit.timestamp)}
          <sl-icon name=dot></sl-icon>
          <h-box style="gap: var(--sl-spacing-2x-small)">
            <sl-icon library=boxicons name=bx-git-commit></sl-icon>
            ${this._renderShortSha(commit)}
          </h-box>
          <slot name=details></slot>
        </h-box>
      </v-box>
    `
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
      min-width: 10em;
    }

    .container {
      gap: var(--sl-spacing-x-small);
    }

    :host([large]) .container {
      gap: var(--sl-spacing-small);
    }

    sl-tooltip {
      --max-width: 40em;
    }

    .abbr-sha {
      font-size: 90%;
      font-family: var(--sl-font-mono);
      overflow: hidden;
      display: inline-block;
      vertical-align: bottom;
      color: #1976d2 !important;
    }

    .pr-link {
      color: #1976d2 !important;
    }

    .title-row {
      gap: var(--sl-spacing-2x-small);
      align-items: center;
      overflow: hidden;
    }

    .commit-title {
      font-weight: var(--sl-font-weight-semibold);
      font-size: var(--sl-font-size-medium);
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    :host([large]) .commit-title {
      font-size: var(--sl-font-size-2x-large);
      font-weight: normal;
    }

    .commit-description {
      gap: var(--sl-spacing-2x-small);
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);

      sl-avatar {
        --size: 16px;
      }
    }

    .refs-block {
      margin-left: var(--sl-spacing-small);
      margin-block: -5px;
      flex: auto;
      flex-grow: 0;
    }

    pr-label, branch-label {
      font-size: var(--sl-font-size-small);

      :host([large]) & {
        font-size: var(--sl-font-size-large);
      }
    }

    .shortsha {
      font-family: var(--sl-font-mono);
    }
  `];
}
